import React from 'react';
import { Button, Modal, Form } from 'react-bootstrap';
import {
  RISK_AMOUNT_OPTIONS,
  STOP_ATR_MULTIPLIER,
  calculateRiskPosition,
  calculateBuyQuantity
} from '../utils/riskProfile';

const formatDollars = (value, fractionDigits = 0) => `$${value.toLocaleString(undefined, {
  minimumFractionDigits: fractionDigits,
  maximumFractionDigits: fractionDigits
})}`;

const RiskProfileModal = ({
  show,
  onBack,
  symbol,
  summary,
  entryPrice,
  availableCash,
  riskAmount,
  onRiskAmountChange,
  atrPercent,
  onAtrPercentChange,
  loading,
  onConfirm
}) => {
  const position = calculateRiskPosition(riskAmount, atrPercent);
  const cash = Number.parseFloat(availableCash) || 0;
  const quantity = position && entryPrice
    ? calculateBuyQuantity(position.positionSize, entryPrice, cash)
    : null;
  const cappedByCash = position != null && position.positionSize > cash;
  const effectiveRisk = position && quantity != null
    ? quantity * entryPrice * (position.stopDistancePercent / 100)
    : null;

  const handleSubmit = (event) => {
    event.preventDefault();
    if (position && !loading) {
      onConfirm(position.positionSize);
    }
  };

  return (
    <Modal show={show} onHide={onBack} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Risk Profile for {symbol}</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <div className="row g-4">
            <div className="col-md-6">
              <div className="mb-3">{summary}</div>
              <Form.Group className="mb-3" controlId="risk-profile-amount">
                <Form.Label>Total risk</Form.Label>
                <Form.Select
                  value={riskAmount}
                  onChange={(event) => onRiskAmountChange(Number(event.target.value))}
                  disabled={loading}
                >
                  {RISK_AMOUNT_OPTIONS.map((amount) => (
                    <option key={amount} value={amount}>{formatDollars(amount)}</option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Form.Group className="mb-3" controlId="risk-profile-atr">
                <Form.Label>21-day ATR (%)</Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  step="any"
                  placeholder="e.g. 5"
                  value={atrPercent}
                  onChange={(event) => onAtrPercentChange(event.target.value)}
                  disabled={loading}
                  autoFocus
                />
              </Form.Group>
            </div>
            <div className="col-md-6">
              <div className="border rounded p-3 h-100" data-testid="risk-profile-breakdown">
                <h6 className="mb-3">Results</h6>
                {position ? (
                  <>
                    <div className="d-flex justify-content-between">
                      <span>Stop distance ({STOP_ATR_MULTIPLIER}× ATR)</span>
                      <strong>{position.stopDistancePercent.toFixed(2)}%</strong>
                    </div>
                    <div className="d-flex justify-content-between">
                      <span>Position size</span>
                      <strong>{formatDollars(position.positionSize)}</strong>
                    </div>
                    {entryPrice && (
                      <>
                        <div className="d-flex justify-content-between">
                          <span>Shares @ {formatDollars(entryPrice, 2)}</span>
                          <strong>{quantity}</strong>
                        </div>
                        <div className="d-flex justify-content-between">
                          <span>Stop price</span>
                          <strong>{formatDollars(entryPrice * (1 - position.stopDistancePercent / 100), 2)}</strong>
                        </div>
                      </>
                    )}
                    {cappedByCash && (
                      <div className="text-warning mt-2">
                        Exceeds available cash; order will be capped
                        {effectiveRisk != null && <> (actual risk ≈ {formatDollars(effectiveRisk)})</>}.
                      </div>
                    )}
                  </>
                ) : atrPercent !== '' ? (
                  <div className="text-danger">Enter an ATR between 0% and 50%.</div>
                ) : (
                  <div className="text-muted">Enter the 21-day ATR to size the position.</div>
                )}
              </div>
            </div>
          </div>
          {loading && (
            <div className="text-center mt-3">Creating order...</div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onBack} disabled={loading}>
            Back
          </Button>
          <Button variant="success" type="submit" disabled={!position || loading}>
            {loading && (
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
            )}
            {position ? `Buy ${formatDollars(position.positionSize)}` : 'Buy'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default RiskProfileModal;
